import { Workbox } from "workbox-window";
import { addDebugLog } from "../hooks/use-debug-logs.hook";

const isServiceWorkerEnabled = () => "serviceWorker" in navigator;

// Écouter les messages du service worker pour les logs de débogage
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  const handleServiceWorkerMessage = (event: Event) => {
    const messageEvent = event as MessageEvent;
    if (messageEvent.data && messageEvent.data.type === 'DEBUG_LOG') {
      addDebugLog('service-worker', messageEvent.data.message);
    }
  };
  
  // Écouter les messages du service worker
  navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
  
  // Écouter aussi quand le service worker devient disponible
  navigator.serviceWorker.ready.then(() => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.addEventListener('message', handleServiceWorkerMessage);
    }
  });
}

// Instance unique de Workbox pour réutiliser la connexion
let workboxInstance: Workbox | null = null;

const getWorkboxInstance = async (): Promise<Workbox> => {
  if (!workboxInstance) {
    console.log("[ServiceWorkerService] Creating new Workbox instance");
    workboxInstance = new Workbox("/service-worker.js");
    await workboxInstance.register();
    console.log("[ServiceWorkerService] Workbox instance registered");
    
    // Attendre que le service worker soit actif
    await navigator.serviceWorker.ready;
    console.log("[ServiceWorkerService] Service worker is ready");
  } else {
    console.log("[ServiceWorkerService] Reusing existing Workbox instance");
    // S'assurer que le service worker est toujours actif
    await navigator.serviceWorker.ready;
  }
  return workboxInstance;
};

const sendMessage = async (type: string, payload: any): Promise<any> => {
  console.log("[ServiceWorkerService] Sending message:", type, payload);
  try {
    const workbox = await getWorkboxInstance();
    console.log("[ServiceWorkerService] Workbox instance:", workbox);
    console.log("[ServiceWorkerService] Service worker controller:", navigator.serviceWorker.controller);
    console.log("[ServiceWorkerService] Service worker ready state:", navigator.serviceWorker.ready);
    
    // Vérifier que le service worker est bien actif
    if (!navigator.serviceWorker.controller) {
      console.warn("[ServiceWorkerService] No service worker controller found, waiting...");
      await navigator.serviceWorker.ready;
      console.log("[ServiceWorkerService] Service worker is now ready");
    }
    
    console.log("[ServiceWorkerService] Sending message to service worker...");
    
    // Essayer d'abord avec Workbox messageSW
    let messagePromise: Promise<any>;
    try {
      messagePromise = workbox.messageSW({ type, payload });
      console.log("[ServiceWorkerService] Message promise created via Workbox, waiting for response...");
    } catch (workboxError) {
      console.warn("[ServiceWorkerService] Workbox messageSW failed, trying direct postMessage:", workboxError);
      // Fallback: utiliser directement postMessage avec MessageChannel
      messagePromise = new Promise((resolve, reject) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          console.log("[ServiceWorkerService] Response received via MessageChannel:", event.data);
          resolve(event.data);
        };
        
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage(
            { type, payload },
            [messageChannel.port2]
          );
          console.log("[ServiceWorkerService] Message sent via direct postMessage");
        } else {
          reject(new Error("No service worker controller available"));
        }
      });
    }
    
    const result = await messagePromise;
    
    console.log("[ServiceWorkerService] Message response received:", result);
    return result;
  } catch (error) {
    console.error("[ServiceWorkerService] Error sending message:", error);
    throw error;
  }
};

export const ServiceWorkerService = {
  isServiceWorkerEnabled,
  sendMessage,
};
